import { CSSProperties } from 'react';
import { Box, CircularProgress, Divider, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarIcon from '@mui/icons-material/CalendarToday';

import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import RichTextEditor, { EditorValue } from 'react-rte';

import { CompanyContact, Contact } from 'components/shared/Contacts';
import { Badge } from 'components/jobs/Badge';
import Addon from 'components/shared/Addon';

import { StatusToColor, StatusMappings } from 'dtos/jobs/ReportStatus';
import { DiligenceStore } from 'stores/diligence/DiligenceStore';

import { addressFormatter } from 'utils/formatter';
import { googleMapsApiKey, salesforceTimeZone } from 'utils/config';

import { ReactComponent as BoweryLogo } from 'images/boweryLogo.svg';
import { JobSummary } from './JobSummary';
import { toolbarConfig } from 'utils/editorToolbarConfig';
import { Diligence } from './Diligence';
import { AdminModal, ShareAccessModal } from './modals';
import { Inspection } from './Inspection';

function renderDate(date: string): string {
  return DateTime.fromISO(date).setZone(salesforceTimeZone).toFormat('MM/dd/yy');
}

type PropertyImageProps = {
  address: string;
  isLoading: boolean;
  coordinates?: { latitude: number; longitude: number };
  style?: CSSProperties;
};

const PropertyImage = observer(({ address, coordinates, isLoading, style = {} }: PropertyImageProps) => {
  const streetViewUrl = `https://www.google.com/maps/embed/v1/streetview?location=${coordinates?.latitude},${coordinates?.longitude}&key=${googleMapsApiKey}`;
  const mapUrl = `https://www.google.com/maps/embed/v1/place?q=${encodeURIComponent(address)}&key=${googleMapsApiKey}`;

  if (isLoading)
    return (
      <Box data-testid="property-image-loader">
        <CircularProgress />
      </Box>
    );

  if (!address) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'grey.100',
          aspectRatio: '3/2',
          maxWidth: '100%',
        }}
        style={style}
      >
        <BusinessIcon fontSize="large" />
        <Typography variant="overline" letterSpacing="1px" marginTop="8px">
          Property View Not Available
        </Typography>
      </Box>
    );
  }

  const src = coordinates ? streetViewUrl : mapUrl;

  return (
    <iframe
      title={`Map view of ${address}`}
      data-testid="property-map"
      style={{ border: 0, aspectRatio: '3/2', maxWidth: '100%', ...style }}
      loading="lazy"
      allowFullScreen
      referrerPolicy="no-referrer-when-downgrade"
      src={src}
      key={src} // Trigger a remount to avoid iframe navigation issues
    />
  );
});

type JobDetailsProps = {
  diligenceStore: DiligenceStore;
  propertyCoordinates?: { latitude: number; longitude: number };
  propertyCoordinatesAreLoading: boolean;
};

const JobDetails = observer(
  ({ diligenceStore, propertyCoordinates, propertyCoordinatesAreLoading }: JobDetailsProps) => {
    const { job, addonStore, clientNotes, setClientNotes, adminStore, adminDetails, shareAccessStore } = diligenceStore;

    const theme = useTheme();
    const xsScreen = useMediaQuery(theme.breakpoints.only('xs'));
    const atLeastSmScreen = useMediaQuery(theme.breakpoints.up('sm'));
    const atLeastMdScreen = useMediaQuery(theme.breakpoints.up('md'));
    const smallerThanMdScreen = useMediaQuery(theme.breakpoints.down('md'));
    const horizontalPadding = atLeastSmScreen ? '64px' : '24px';

    const address = addressFormatter(job.address);

    const propertyImage = (
      <PropertyImage
        address={address}
        coordinates={propertyCoordinates}
        isLoading={propertyCoordinatesAreLoading}
        style={{ width: !xsScreen ? '480px' : '327px' }}
      />
    );

    return (
      <>
        <Grid
          container
          item
          sx={{ marginBottom: '60px' }}
          p={{ xs: `8px ${horizontalPadding} 0 ${horizontalPadding}`, sm: `0 ${horizontalPadding}` }}
        >
          <Grid item xs={12} md={6}>
            <Grid container item>
              <Grid item xs={12} marginBottom="8px">
                <Typography
                  fontFamily="Source Serif Pro, serif"
                  fontSize="36px"
                  fontWeight={700}
                  lineHeight="116.7%"
                  data-testid="job-address"
                >
                  {address}
                </Typography>
              </Grid>
              {job.accessLevel === 'job' && job.reportStatus && (
                <Grid item xs="auto" marginRight="24px" marginBottom="8px">
                  <Badge status={StatusToColor[job.reportStatus]} variant="caption" data-testid="job-status">
                    {`${StatusMappings[job.reportStatus]} `}
                  </Badge>
                </Grid>
              )}
              {job.assetType && (
                <Grid item xs="auto" letterSpacing="0.4px">
                  <Typography variant="caption" sx={{ marginRight: '24px', textTransform: 'uppercase' }}>
                    {job.assetType} Property
                  </Typography>
                </Grid>
              )}
              {job.accessLevel === 'job' && job.name && (
                <Grid item xs="auto" letterSpacing="0.4px">
                  <Typography variant="caption">{job.name}</Typography>
                </Grid>
              )}
            </Grid>

            {!atLeastMdScreen && (
              <Grid container item>
                <Grid
                  item
                  xs={12}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  paddingTop="32px"
                  paddingBottom="32px"
                >
                  {propertyImage}
                </Grid>
              </Grid>
            )}

            {job.accessLevel === 'job' && (
              <>
                <Grid container item marginTop={{ md: '40px' }}>
                  <Grid container spacing={1} alignItems="stretch" item xs={12} letterSpacing="0.15px">
                    <Grid item>
                      <CalendarIcon sx={{ fillOpacity: 0.54 }} />
                    </Grid>
                    <Grid item>
                      <Typography variant="subtitle1" display="inline">
                        Report Due Date
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="body2" display="inline" fontSize="16px" fontWeight={700} lineHeight="175%">
                        {job.dueDate ? renderDate(job.dueDate) : 'Unavailable'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container item marginTop="16px">
                  <Grid item xs={12}>
                    <Box
                      minHeight="132px"
                      padding="24px"
                      borderRadius="8px"
                      bgcolor="grey.100"
                      maxWidth={{ md: '557px' }}
                    >
                      {addonStore && (
                        <Addon
                          addonStore={addonStore}
                          fullWidthButton={smallerThanMdScreen}
                          jobDetails={{ job: job.name, client: job.client }}
                        />
                      )}

                      <Box display="flex" alignItems="center" marginTop="16px">
                        {!xsScreen && (
                          <div style={{ marginLeft: '8px', marginRight: '16px' }}>
                            <BoweryLogo title="Bowery logo" />
                          </div>
                        )}
                        <Typography variant="body1">
                          Expedite your report turn-time, get photos sooner, and more. Click here to see how we can
                          enhance your appraisal experience.
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}
            <Grid container spacing={3} item marginTop="24px">
              <Grid item xs={12} marginBottom={{ sm: '-16px' }}>
                <Typography
                  variant="subtitle1"
                  fontSize="16px"
                  fontWeight={700}
                  lineHeight="175%"
                  letterSpacing="0.15px"
                >
                  Job Contacts
                </Typography>
              </Grid>
              {job.contacts.leadAppraiser.name && (
                <Grid item xs={12} sm={6} sx={{ overflowX: 'clip' }}>
                  <Contact
                    title="Lead Appraiser"
                    data-testid="avatar-lead-appraiser"
                    contact={job.contacts.leadAppraiser}
                    tooltip
                  />
                </Grid>
              )}
              {job.contacts.appraiser.name && (
                <Grid item xs={12} sm={6} sx={{ overflowX: 'clip' }}>
                  <Contact title="Appraiser" data-testid="avatar-appraiser" contact={job.contacts.appraiser} tooltip />
                </Grid>
              )}
              {job.contacts.clientServices.name && (
                <Grid item xs={12} sm={6} sx={{ overflowX: 'clip' }}>
                  <Contact
                    title="Client Services Contact"
                    data-testid="avatar-client-services"
                    contact={job.contacts.clientServices}
                    tooltip
                  />
                </Grid>
              )}
              {job.client.company && (
                <Grid item xs={12} sm={6} sx={{ overflowX: 'clip' }}>
                  <CompanyContact title="Client" data-testid="avatar-client" name={job.client.company} />
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ marginTop: '40px', display: 'flex' }}>
                  <strong>Comments and Notes</strong>
                </Typography>
                <Typography variant="body2" sx={{ marginTop: '4px' }}>
                  Add any notes, descriptions or feedback.
                </Typography>
                <div style={{ maxWidth: '960px', width: '100%' }}>
                  <RichTextEditor
                    rootStyle={{ maxWidth: '100%', width: '100%', boxSizing: 'border-box', overflow: 'auto' }}
                    data-testid="appraiser-notes"
                    editorClassName="clientNotesEditor"
                    value={clientNotes}
                    toolbarConfig={toolbarConfig}
                    onChange={(value: EditorValue) => setClientNotes(value)}
                  />
                </div>
              </Grid>
            </Grid>
          </Grid>

          {atLeastMdScreen && (
            <Grid item md={6} marginTop="50px" display="flex" alignItems="center" flexDirection="column">
              <JobSummary diligenceStore={diligenceStore} />
              {propertyImage}
            </Grid>
          )}

          <Divider />

          <Grid item xs={12}>
            <Divider sx={{ marginY: '32px' }} />
            <Grid container>
              <Grid item xs={12} md={6}>
                <Diligence diligenceStore={diligenceStore} />
              </Grid>

              {job.accessLevel === 'job' && job.inspection && (
                <Grid item xs={12} md={6}>
                  <Inspection diligenceStore={diligenceStore} />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        {adminStore && <AdminModal store={adminStore} adminDetails={adminDetails} />}
        {shareAccessStore && <ShareAccessModal store={shareAccessStore} jobNumber={adminDetails?.jobNumber} />}
      </>
    );
  }
);

export { JobDetails };
